/**
 * Coordinate transformation utilities for PDF highlighting
 * Handles conversion between normalized coordinates and screen pixels
 * Requirements: 3.2, 3.4
 */

import { 
  BoundingBox, 
  ScreenCoordinates, 
  HighlightRegion,
  HighlightStyle 
} from '@/types';
import { PDFPageViewport } from '@/types/context';
import { 
  CoordinateTransformParams,
  TransformationResult,
  BoundingBoxValidator 
} from '@/types/utils';

/**
 * Default highlight style configuration
 */
const DEFAULT_HIGHLIGHT_STYLE: HighlightStyle = {
  backgroundColor: '#ffeb3b', // Material-UI yellow
  borderColor: '#f57f17',     // Darker yellow
  opacity: 0.4
};

/**
 * Validates that a bounding box has valid normalized coordinates
 */
export const validateBoundingBox: BoundingBoxValidator = (bbox: BoundingBox): boolean => {
  try {
    if (!bbox) {
      console.warn('Bounding box validation failed: bbox is null or undefined');
      return false;
    }

    if (typeof bbox.x !== 'number' || typeof bbox.y !== 'number' || 
        typeof bbox.width !== 'number' || typeof bbox.height !== 'number') {
      console.warn('Bounding box validation failed: coordinates must be numbers', bbox);
      return false;
    }

    if (isNaN(bbox.x) || isNaN(bbox.y) || isNaN(bbox.width) || isNaN(bbox.height)) {
      console.warn('Bounding box validation failed: coordinates contain NaN values', bbox);
      return false;
    }

    if (bbox.x < 0 || bbox.x > 1 || bbox.y < 0 || bbox.y > 1) {
      console.warn('Bounding box validation failed: coordinates must be normalized (0-1)', bbox);
      return false;
    }

    if (bbox.width <= 0 || bbox.height <= 0) {
      console.warn('Bounding box validation failed: dimensions must be positive', bbox);
      return false;
    }

    if (bbox.x + bbox.width > 1.01 || bbox.y + bbox.height > 1.01) { // Allow small tolerance
      console.warn('Bounding box validation failed: extends beyond document boundaries', bbox);
      return false;
    }

    if (typeof bbox.page !== 'number' || bbox.page < 1) {
      console.warn('Bounding box validation failed: page must be a positive integer', bbox);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Bounding box validation error:', error);
    return false;
  }
};

/**
 * Transforms normalized BoundingBox coordinates to screen pixel coordinates
 * Handles PDF coordinate system conversion (bottom-left to top-left origin)
 */
export const transformBoundingBoxToScreen = (
  params: CoordinateTransformParams
): TransformationResult => {
  const { boundingBox, viewport } = params;

  // Validate input parameters
  if (!validateBoundingBox(boundingBox)) {
    return {
      success: false,
      error: 'Invalid bounding box coordinates'
    };
  }

  if (!viewport || viewport.width <= 0 || viewport.height <= 0) {
    return {
      success: false,
      error: 'Invalid viewport dimensions'
    };
  }

  try {
    // Convert normalized coordinates to PDF coordinate system
    const pdfX = boundingBox.x * viewport.width;
    const pdfY = boundingBox.y * viewport.height;
    const pdfWidth = boundingBox.width * viewport.width;
    const pdfHeight = boundingBox.height * viewport.height;

    // Azure Document Intelligence uses top-left origin, same as screen coordinates
    // No need to flip Y coordinate, no pan offset needed for fixed positioning
    const screenX = pdfX;
    const screenY = pdfY;
    const screenWidth = pdfWidth;
    const screenHeight = pdfHeight;

    // Use coordinates directly without zoom scaling (fixed scale = 1.0)
    const scaledCoordinates: ScreenCoordinates = {
      x: screenX,
      y: screenY,
      width: screenWidth,
      height: screenHeight
    };

    return {
      success: true,
      coordinates: scaledCoordinates
    };
  } catch (error) {
    return {
      success: false,
      error: `Coordinate transformation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Transforms multiple bounding boxes to highlight regions with comprehensive error handling
 */
export const transformBoundingBoxesToHighlights = (
  fieldId: string,
  boundingBoxes: BoundingBox[],
  viewport: PDFPageViewport,
  customStyle?: Partial<HighlightStyle>
): HighlightRegion[] => {
  const style = { ...DEFAULT_HIGHLIGHT_STYLE, ...customStyle };
  const highlights: HighlightRegion[] = [];
  const errors: string[] = [];

  // Validate inputs
  if (!fieldId) {
    console.warn('transformBoundingBoxesToHighlights: fieldId is required');
    return highlights;
  }

  if (!Array.isArray(boundingBoxes) || boundingBoxes.length === 0) {
    console.warn(`transformBoundingBoxesToHighlights: No bounding boxes provided for field ${fieldId}`);
    return highlights;
  }

  if (!viewport) {
    console.warn(`transformBoundingBoxesToHighlights: Viewport not available for field ${fieldId}`);
    return highlights;
  }

  try {
    for (let i = 0; i < boundingBoxes.length; i++) {
      const bbox = boundingBoxes[i];
      
      try {
        const transformResult = transformBoundingBoxToScreen({
          boundingBox: bbox,
          viewport
        });

        if (transformResult.success && transformResult.coordinates) {
          highlights.push({
            fieldId,
            page: bbox.page,
            screenCoordinates: transformResult.coordinates,
            style
          });
        } else {
          const errorMsg = `Bounding box ${i}: ${transformResult.error}`;
          errors.push(errorMsg);
          console.warn(`Failed to transform bounding box for field ${fieldId}:`, errorMsg);
        }
      } catch (error) {
        const errorMsg = `Bounding box ${i}: ${error instanceof Error ? error.message : 'Unknown error'}`;
        errors.push(errorMsg);
        console.error(`Error transforming bounding box for field ${fieldId}:`, error);
      }
    }

    // Log summary if there were errors
    if (errors.length > 0) {
      console.warn(`Field ${fieldId}: ${errors.length}/${boundingBoxes.length} bounding boxes failed transformation`);
    }

    return highlights;
  } catch (error) {
    console.error(`Critical error in transformBoundingBoxesToHighlights for field ${fieldId}:`, error);
    return highlights;
  }
};

/**
 * Calculates the optimal zoom level to fit content within container
 */
export const calculateFitToWidthZoom = (
  containerWidth: number,
  pdfPageWidth: number,
  padding: number = 20
): number => {
  const availableWidth = containerWidth - (padding * 2);
  const zoomLevel = availableWidth / pdfPageWidth;
  
  // Clamp zoom level to reasonable bounds
  return Math.max(0.25, Math.min(3.0, zoomLevel));
};

/**
 * Calculates the optimal zoom level to fit entire page within container
 */
export const calculateFitToPageZoom = (
  containerWidth: number,
  containerHeight: number,
  pdfPageWidth: number,
  pdfPageHeight: number,
  padding: number = 20
): number => {
  const availableWidth = containerWidth - (padding * 2);
  const availableHeight = containerHeight - (padding * 2);
  
  const widthZoom = availableWidth / pdfPageWidth;
  const heightZoom = availableHeight / pdfPageHeight;
  
  // Use the smaller zoom to ensure entire page fits
  const zoomLevel = Math.min(widthZoom, heightZoom);
  
  // Clamp zoom level to reasonable bounds
  return Math.max(0.25, Math.min(3.0, zoomLevel));
};

/**
 * Converts screen coordinates back to normalized coordinates
 * Useful for reverse transformations or coordinate validation
 */
export const transformScreenToBoundingBox = (
  screenCoords: ScreenCoordinates,
  viewport: PDFPageViewport,
  zoomLevel: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
): BoundingBox | null => {
  if (!viewport || viewport.width <= 0 || viewport.height <= 0) {
    return null;
  }

  try {
    // Reverse zoom scaling
    const unscaledX = screenCoords.x / zoomLevel;
    const unscaledY = screenCoords.y / zoomLevel;
    const unscaledWidth = screenCoords.width / zoomLevel;
    const unscaledHeight = screenCoords.height / zoomLevel;

    // Remove pan offset
    const pdfX = unscaledX - panOffset.x;
    const pdfY = unscaledY - panOffset.y;

    // Convert from screen coordinates (top-left origin) to PDF coordinates (bottom-left origin)
    const normalizedX = pdfX / viewport.width;
    const normalizedY = (viewport.height - (pdfY + unscaledHeight)) / viewport.height;
    const normalizedWidth = unscaledWidth / viewport.width;
    const normalizedHeight = unscaledHeight / viewport.height;

    const boundingBox: BoundingBox = {
      page: 1, // Page number should be provided separately
      x: normalizedX,
      y: normalizedY,
      width: normalizedWidth,
      height: normalizedHeight
    };

    // Validate the result
    if (validateBoundingBox(boundingBox)) {
      return boundingBox;
    } else {
      console.warn('Reverse transformation resulted in invalid bounding box');
      return null;
    }
  } catch (error) {
    console.error('Screen to bounding box transformation failed:', error);
    return null;
  }
};

/**
 * Utility to check if two bounding boxes overlap
 */
export const boundingBoxesOverlap = (bbox1: BoundingBox, bbox2: BoundingBox): boolean => {
  if (bbox1.page !== bbox2.page) {
    return false;
  }

  const left1 = bbox1.x;
  const right1 = bbox1.x + bbox1.width;
  const top1 = bbox1.y;
  const bottom1 = bbox1.y + bbox1.height;

  const left2 = bbox2.x;
  const right2 = bbox2.x + bbox2.width;
  const top2 = bbox2.y;
  const bottom2 = bbox2.y + bbox2.height;

  return !(right1 <= left2 || right2 <= left1 || bottom1 <= top2 || bottom2 <= top1);
};

/**
 * Merges overlapping bounding boxes into larger regions
 * Useful for optimizing highlight rendering performance
 */
export const mergeOverlappingBoundingBoxes = (boundingBoxes: BoundingBox[]): BoundingBox[] => {
  if (boundingBoxes.length <= 1) {
    return [...boundingBoxes];
  }

  const merged: BoundingBox[] = [];
  const processed = new Set<number>();

  for (let i = 0; i < boundingBoxes.length; i++) {
    if (processed.has(i)) continue;

    let currentBox = { ...boundingBoxes[i] };
    processed.add(i);

    // Find all overlapping boxes
    let foundOverlap = true;
    while (foundOverlap) {
      foundOverlap = false;
      
      for (let j = 0; j < boundingBoxes.length; j++) {
        if (processed.has(j)) continue;
        
        if (boundingBoxesOverlap(currentBox, boundingBoxes[j])) {
          // Merge the boxes
          const otherBox = boundingBoxes[j];
          const minX = Math.min(currentBox.x, otherBox.x);
          const minY = Math.min(currentBox.y, otherBox.y);
          const maxX = Math.max(currentBox.x + currentBox.width, otherBox.x + otherBox.width);
          const maxY = Math.max(currentBox.y + currentBox.height, otherBox.y + otherBox.height);
          
          currentBox = {
            page: currentBox.page,
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY
          };
          
          processed.add(j);
          foundOverlap = true;
        }
      }
    }

    merged.push(currentBox);
  }

  return merged;
};